import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { createStore, useStore, type StoreApi } from "zustand";
import type { BarangayOption } from "@rbim/shared";
import { defaultAddNewDataValues, type AddNewDataFormValues } from "./modules/add-new-data/lib/schema";
import { authoritativeHouseholdLocation, type LocationContext } from "../rbim-questionnaire/questionnaire-model";
import { withCurrentManilaDateEncoded } from "./date-encoded";

export type WebWorkspaceProps = {
  context: LocationContext;
  barangayId: string;
  barangays: BarangayOption[];
  encoderName: string;
  draftOwnerId: string;
  assignedLocationLabel: string;
  geographyLoading: boolean;
  geographyError: string;
  recordCreatePath?: "/records" | "/barangay/records";
  recordUpdatePath?: string;
  initialValues?: AddNewDataFormValues;
  preserveInitialDateEncoded?: boolean;
  onRecordSaved?: (id: string) => void;
  onBarangayChange: (id: string) => void;
};
export type DraftState = {
  draft: AddNewDataFormValues | null;
  lastSavedAt: string | null;
  storageError: string | null;
  saveDraft: (draft: AddNewDataFormValues) => void;
  clearDraft: () => void;
};
export function desktopDraftKey(userId: string, municipalityId: string, barangayId: string) {
  return `rbim:desktop-draft:v1:${encodeURIComponent(userId)}:${encodeURIComponent(municipalityId)}:${encodeURIComponent(barangayId || "unselected")}`;
}
export function createDesktopDraftStore(
  key: string,
  seed?: AddNewDataFormValues,
  preserveDateEncoded = false,
) {
  const normalize = (values: AddNewDataFormValues) =>
    preserveDateEncoded ? structuredClone(values) : withCurrentManilaDateEncoded(values);
  let draft = seed ? normalize(seed) : null;
  let lastSavedAt: string | null = null;
  let storageError: string | null = null;
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(key);
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved.version === 1 && saved.draft?.household && saved.draft?.householdQuestions &&
          saved.draft?.encoding && Array.isArray(saved.draft?.members) && Array.isArray(saved.draft?.interviews)) {
        draft = normalize(saved.draft);
        lastSavedAt = typeof saved.lastSavedAt === "string" ? saved.lastSavedAt : null;
      }
    }
  } catch { storageError = "Browser draft storage is unavailable. Keep this form open until it is submitted."; }
  return createStore<DraftState>((set) => ({
    draft, lastSavedAt, storageError,
    saveDraft(values) {
      const timestamp = new Date().toISOString();
      const currentValues = normalize(values);
      try {
        localStorage.setItem(key, JSON.stringify({ version: 1, draft: currentValues, lastSavedAt: timestamp }));
        set({ draft: currentValues, lastSavedAt: timestamp, storageError: null });
      } catch { set({ draft: currentValues, storageError: "Draft could not be saved to browser storage. Keep this form open until it is submitted." }); }
    },
    clearDraft() {
      try {
        localStorage.removeItem(key);
        set({ draft: null, lastSavedAt: null, storageError: null });
      } catch { set({ storageError: "Browser draft could not be cleared. Please try again." }); }
    },
  }));
}
const AdapterContext = createContext<(WebWorkspaceProps & {
  draftStore: StoreApi<DraftState>;
  location: ReturnType<typeof authoritativeHouseholdLocation>;
  defaults: AddNewDataFormValues;
  changeBarangay: (id: string, values: AddNewDataFormValues) => void;
}) | null>(null);

export function WebAdapterProvider({ children, seed, onSelection, ...props }: WebWorkspaceProps & {
  children: ReactNode;
  seed?: AddNewDataFormValues | undefined;
  onSelection: (id: string, values: AddNewDataFormValues) => void;
}) {
  const [draftStore] = useState(() => createDesktopDraftStore(
    desktopDraftKey(props.draftOwnerId, props.context.municipality.id, props.barangayId),
    seed ?? props.initialValues,
    props.preserveInitialDateEncoded === true,
  ));
  const location = useMemo(() => {
    const authoritativeLocation = authoritativeHouseholdLocation(props.context);
    return {
      ...authoritativeLocation,
      province: assignedProvinceFromLocationLabel(
        props.assignedLocationLabel,
        authoritativeLocation.province,
      ),
    };
  }, [props.assignedLocationLabel, props.context]);
  const defaults = useMemo(() => withCurrentManilaDateEncoded({
    ...structuredClone(defaultAddNewDataValues),
    encoding: { ...defaultAddNewDataValues.encoding, encoder: props.encoderName, supervisor: "" },
  }), [props.encoderName]);
  return <AdapterContext.Provider value={{ ...props, draftStore, location, defaults, changeBarangay: onSelection }}>{children}</AdapterContext.Provider>;
}
export function useWebAdapter() {
  const adapter = useContext(AdapterContext);
  if (!adapter) throw new Error("Desktop workspace requires its Municipality web adapter.");
  return adapter;
}
export function useDesktopDraft<T>(selector: (state: DraftState) => T) {
  return useStore(useWebAdapter().draftStore, selector);
}
export const WorkspaceRootContext = createContext<ShadowRoot | null>(null);
export function useWorkspaceRoot() { return useContext(WorkspaceRootContext); }

export function assignedProvinceFromLocationLabel(locationLabel: string, fallback: string) {
  const parts = locationLabel
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length >= 2 ? parts[parts.length - 1] : fallback;
}
